import { config } from "@/shared/lib/config";
import { fetcher, handleError } from "@/shared/lib/fetcher";

export type AudienceVoteEventPhase = "draft" | "scheduled" | "open" | "closed";

export type AudienceVoteEventDTO = {
  id: string;
  slug: string | null;
  title: string;
  status: "draft" | "open" | "closed";
  opensAt: string | null;
  closesAt: string | null;
  showResults: boolean;
  createdAt: string;
  updatedAt: string;
  phase?: AudienceVoteEventPhase;
};

export type AudienceVoteContestantDTO = {
  id: string;
  eventId: string;
  displayName: string;
  subtitle: string | null;
  panelLabel: string | null;
  photoUrl: string | null;
  sortOrder: number;
  status: "draft" | "live" | "withdrawn";
  sourceResponseId: string | null;
  createdAt: string;
  updatedAt: string;
  voteCount?: number;
};

export type AudienceVoteEventDetailDTO = {
  event: AudienceVoteEventDTO;
  phase: AudienceVoteEventPhase;
  stats: {
    uniqueVoters: number;
    totalVotes: number;
  };
  contestants: AudienceVoteContestantDTO[];
};

export type UpdateAudienceVoteEventBody = Partial<
  Pick<
    AudienceVoteEventDTO,
    "slug" | "title" | "status" | "opensAt" | "closesAt" | "showResults"
  >
>;

export type UpsertAudienceVoteContestantBody = {
  displayName?: string;
  subtitle?: string | null;
  panelLabel?: string | null;
  photoUrl?: string | null;
  sortOrder?: number;
  status?: "draft" | "live" | "withdrawn";
  sourceResponseId?: string | null;
};

export const audienceVoteApi = {
  listEvents: () =>
    fetcher.get<{ items: AudienceVoteEventDTO[] }>(
      `${config.apiUrl}/audience-vote/events`,
    ),

  getEvent: (eventId: string) =>
    fetcher.get<AudienceVoteEventDetailDTO>(
      `${config.apiUrl}/audience-vote/events/${eventId}`,
    ),

  updateEvent: (eventId: string, body: UpdateAudienceVoteEventBody) =>
    fetcher.put<AudienceVoteEventDTO>(
      `${config.apiUrl}/audience-vote/events/${eventId}`,
      body,
    ),

  patchEvent: (
    eventId: string,
    body: { status?: AudienceVoteEventDTO["status"]; showResults?: boolean },
  ) =>
    fetcher.patch<AudienceVoteEventDTO>(
      `${config.apiUrl}/audience-vote/events/${eventId}`,
      body,
    ),

  createContestant: (
    eventId: string,
    body: Required<Pick<UpsertAudienceVoteContestantBody, "displayName">> &
      UpsertAudienceVoteContestantBody,
  ) =>
    fetcher.post<AudienceVoteContestantDTO>(
      `${config.apiUrl}/audience-vote/events/${eventId}/contestants`,
      body,
    ),

  updateContestant: (
    eventId: string,
    contestantId: string,
    body: UpsertAudienceVoteContestantBody,
  ) =>
    fetcher.put<AudienceVoteContestantDTO>(
      `${config.apiUrl}/audience-vote/events/${eventId}/contestants/${contestantId}`,
      body,
    ),

  getContestantPhotoReadUrl: (eventId: string, key: string) =>
    fetcher.post<{ url: string }>(
      `${config.apiUrl}/audience-vote/events/${eventId}/contestants/photo-read-url`,
      { key },
    ),

  deleteContestant: async (eventId: string, contestantId: string) => {
    const res = await fetch(
      `${config.apiUrl}/audience-vote/events/${eventId}/contestants/${contestantId}`,
      { method: "DELETE", credentials: "include" },
    );
    if (!res.ok) return handleError(res);
  },

  async downloadTallyCsv(eventId: string): Promise<void> {
    const url = `${config.apiUrl}/audience-vote/events/${eventId}/export.csv`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return handleError(res);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audience-vote-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
};
